import { ComponentProps, ReactNode, useCallback, useState } from "react";
import { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";
import { ScheduleLifecycleVerb } from "@common/types/domains/accounting/schedule";
import { ScheduleEditModal } from "../manual/ScheduleEditModal";
import { ScheduleLifecycleConfirmModal } from "./ScheduleLifecycleConfirmModal";

interface UseScheduleLifecycleActionsOptions {
    /**
     * Runs once the action has succeeded. The applications table uses it to
     * expand the row so the new journal entries are visible; surfaces with
     * nothing to reveal can leave it out, since the mutations invalidate the
     * queries themselves.
     */
    onCompleted?: (request: ScheduleLifecycleRequest) => void;
    /** For surfaces inside an overlay: shows locked periods without replacing that overlay. */
    showLockedPeriods?: ComponentProps<
        typeof ScheduleLifecycleConfirmModal
    >["showLockedPeriods"];
}

interface UseScheduleLifecycleActionsResult {
    requestLifecycleAction: (request: ScheduleLifecycleRequest) => void;
    /** Mount once, at a level that is not inside a popover or a table cell. */
    modals: ReactNode;
}

/**
 * Hosts the two modals every lifecycle action needs, so a surface only has to
 * say which verb the user picked.
 *
 * A `change` with no payload has to collect the edit first, which is why
 * this owns two pieces of state rather than one: the schedule modal gathers
 * the change and stays open under the confirm modal until the change lands.
 * Callers that already know the change (the invoice edit flow syncs the
 * amount from the source) pass it on the request and skip the form.
 */
export function useScheduleLifecycleActions(
    options: UseScheduleLifecycleActionsOptions = {},
): UseScheduleLifecycleActionsResult {
    const { onCompleted, showLockedPeriods } = options;
    const [lifecycleRequest, setLifecycleRequest] =
        useState<ScheduleLifecycleRequest | null>(null);
    const [changeFormRequest, setChangeFormRequest] =
        useState<ScheduleLifecycleRequest | null>(null);

    const requestLifecycleAction = useCallback(
        (request: ScheduleLifecycleRequest) => {
            if (
                request.verb === ScheduleLifecycleVerb.change &&
                !request.change
            ) {
                setChangeFormRequest(request);
                return;
            }
            setLifecycleRequest(request);
        },
        [],
    );

    const handleCompleted = useCallback(
        (request: ScheduleLifecycleRequest) => {
            setLifecycleRequest(null);
            setChangeFormRequest(null);
            onCompleted?.(request);
        },
        [onCompleted],
    );

    const modals = (
        <>
            <ScheduleEditModal
                target={changeFormRequest}
                onHide={() => setChangeFormRequest(null)}
                onRequestChange={(change) => {
                    if (changeFormRequest) {
                        setLifecycleRequest({ ...changeFormRequest, change });
                    }
                }}
            />
            <ScheduleLifecycleConfirmModal
                request={lifecycleRequest}
                onClose={() => setLifecycleRequest(null)}
                onCompleted={handleCompleted}
                showLockedPeriods={showLockedPeriods}
            />
        </>
    );

    return { requestLifecycleAction, modals };
}
